import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    relocationForms: [],
    currentRelocationForm: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Relocation Forms
export const fetchRelocationForms = createAsyncThunk(
    'relocationForms/fetchRelocationForms',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/relocation-forms');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Relocation forms'
            );
        }
    }
);

// Fetch a single Relocation Form
export const fetchRelocationForm = createAsyncThunk(
    'relocationForms/fetchRelocationForm',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/relocation-forms/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Relocation form'
            );
        }
    }
);

// Create a new Relocation Form
export const createRelocationForm = createAsyncThunk(
    'relocationForms/createRelocationForm',
    async (relocationData, { rejectWithValue }) => {
        try {
            const response = await api.post('/relocation-forms', relocationData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create Relocation form'
            );
        }
    }
);

// Update an existing Relocation Form (by ID)
export const updateRelocationForm = createAsyncThunk(
    'relocationForms/updateRelocationForm',
    async ({ case_id, relocationData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/relocation-forms/case/${case_id}`, relocationData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update Relocation form'
            );
        }
    }
);

// Delete a Relocation Form
export const deleteRelocationForm = createAsyncThunk(
    'relocationForms/deleteRelocationForm',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/relocation-forms/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete Relocation form'
            );
        }
    }
);

// ==================== Slice ====================
const relocationFormsSlice = createSlice({
    name: 'relocationForms',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentRelocationForm: (state, action) => {
            state.currentRelocationForm = action.payload;
        },
        clearCurrentRelocationForm: (state) => {
            state.currentRelocationForm = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchRelocationForms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchRelocationForms.fulfilled, (state, action) => {
                state.isLoading = false;
                state.relocationForms = action.payload;
            })
            .addCase(fetchRelocationForms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchRelocationForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchRelocationForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentRelocationForm = action.payload;
            })
            .addCase(fetchRelocationForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createRelocationForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createRelocationForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.relocationForms.unshift(action.payload);
            })
            .addCase(createRelocationForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateRelocationForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateRelocationForm.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.relocationForms.findIndex(
                    (d) => d.id === updated.id
                );
                if (index !== -1) {
                    state.relocationForms[index] = updated;
                }

                if (state.currentRelocationForm?.id === updated.id) {
                    state.currentRelocationForm = updated;
                }
            })
            .addCase(updateRelocationForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteRelocationForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteRelocationForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.relocationForms = state.relocationForms.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentRelocationForm?.id === action.payload) {
                    state.currentRelocationForm = null;
                }
            })
            .addCase(deleteRelocationForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentRelocationForm,
    clearCurrentRelocationForm,
} = relocationFormsSlice.actions;

export default relocationFormsSlice.reducer;
